import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import iiApi from "@/lib/incomingInspectionApi";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const NC_AREAS = ["Machining", "Leak Testing", "Final Inspection", "Others"];
const SOURCE_LABELS = {
  dimension: "Dimension", visual: "Visual Check", fitment: "Fitment Check",
  surface_finish: "Surface Finish Check", thread1: "Thread 1 Check", thread2: "Thread 2 Check",
};

function blankForm() {
  return {
    part_name: "", part_number: "", model: "", nc_date: "", raised_by_name: "", status: "OPEN",
    nc_area: [], nc_details: "", actual_measurement: "", rejection_qty: "", responsibility: "",
    disposition_action: "", rca_occurrence: "", rca_detection: "", why1: "", why2: "", why3: "", why4: "", why5: "",
    action_plan: "", action_responsibility: "", target_date: "", effectiveness_month1: "", effectiveness_month2: "",
    effectiveness_month3: "", doc_update_document: "", doc_update_revno: "", horizontal_deployment: "",
    prepared_by: "", approved_by: "",
  };
}

export default function IncomingInspectionNcr() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("report_id");
  // No report_id → a standalone NCR raised directly from the NCR Register;
  // ncr_id (if present) reopens a previously saved standalone NCR.
  const isStandalone = !reportId;
  const [ncrId, setNcrId] = useState(searchParams.get("ncr_id"));
  const source = searchParams.get("source") || "dimension";
  const dimensionIndex = searchParams.get("dimension_index") ? parseInt(searchParams.get("dimension_index"), 10) : null;
  const backPath = searchParams.get("back") || "/quality/incoming-inspection/history";

  const [form, setForm] = useState(blankForm());
  const [ncrNo, setNcrNo] = useState("—");
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingComplete, setSavingComplete] = useState(false);

  const sourceLabel = isStandalone ? "Manual" : source === "dimension" ? `Dimension #${dimensionIndex ?? ""}` : SOURCE_LABELS[source] || source;

  useEffect(() => {
    (async () => {
      try {
        let data;
        if (isStandalone) {
          data = ncrId ? await iiApi.getNcrById(ncrId) : { found: false, context: {} };
        } else {
          const params = { report_id: reportId, source };
          if (dimensionIndex) params.dimension_index = dimensionIndex;
          data = await iiApi.getNcr(params);
        }
        setContext(data.context || {});
        if (data.found && data.ncr) {
          const n = data.ncr;
          setForm({
            part_name: n.part_name || "", part_number: n.part_number || "", model: n.model || "",
            nc_date: n.nc_date || "", raised_by_name: n.raised_by_name || "", status: n.status || "OPEN",
            nc_area: (n.nc_area || "").split(",").map((s) => s.trim()).filter(Boolean),
            nc_details: n.nc_details || "", actual_measurement: n.actual_measurement || "",
            rejection_qty: n.rejection_qty ?? "", responsibility: n.responsibility || "",
            disposition_action: n.disposition_action || "", rca_occurrence: n.rca_occurrence || "",
            rca_detection: n.rca_detection || "", why1: n.why1 || "", why2: n.why2 || "", why3: n.why3 || "",
            why4: n.why4 || "", why5: n.why5 || "", action_plan: n.action_plan || "",
            action_responsibility: n.action_responsibility || "", target_date: n.target_date || "",
            effectiveness_month1: n.effectiveness_month1 || "", effectiveness_month2: n.effectiveness_month2 || "",
            effectiveness_month3: n.effectiveness_month3 || "", doc_update_document: n.doc_update_document || "",
            doc_update_revno: n.doc_update_revno || "", horizontal_deployment: n.horizontal_deployment || "",
            prepared_by: n.prepared_by || "", approved_by: n.approved_by || "",
          });
          setNcrNo(n.ncr_no || "—");
        } else {
          const ctx = data.context || {};
          setForm((f) => ({
            ...f,
            part_name: ctx.part_description || "",
            part_number: ctx.part_number || "",
            raised_by_name: data.current_user_name || user?.name || "",
            nc_date: new Date().toISOString().slice(0, 10),
            actual_measurement: ctx.actual_measurement || ctx.check_remarks || "",
          }));
          setNcrNo("Not yet raised");
        }
      } catch (err) {
        setError("Could not load this NCR: " + (formatApiError(err?.response?.data?.detail) || err.message));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, source, dimensionIndex]);

  const toggleArea = (area) => setForm((f) => ({
    ...f, nc_area: f.nc_area.includes(area) ? f.nc_area.filter((a) => a !== area) : [...f.nc_area, area],
  }));

  const save = async (complete) => {
    setError("");
    (complete ? setSavingComplete : setSavingDraft)(true);
    try {
      const payload = {
        report_id: reportId || null, ncr_id: isStandalone ? ncrId : null,
        dimension_index: dimensionIndex, source,
        ...form,
        rejection_qty: form.rejection_qty === "" ? null : Number(form.rejection_qty),
        complete: !!complete,
      };
      const res = await iiApi.saveNcr(payload);
      setNcrNo(res.ncr_no || "—");
      if (isStandalone && res.ncr_id && res.ncr_id !== ncrId) {
        // Remember the new NCR so further saves update it rather than raising another.
        setNcrId(res.ncr_id);
        const qs = new URLSearchParams(searchParams);
        qs.set("ncr_id", res.ncr_id);
        nav(`?${qs.toString()}`, { replace: true });
      }
      toast.success(res.message || (complete ? "NCR completed." : "NCR saved."));
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      (complete ? setSavingComplete : setSavingDraft)(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="incoming-inspection-ncr-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>CAPL — Non-Conformance Report</h1>
          <p className="text-sm text-slate-500 mt-1">Reproduces the CAPL NCR format (CAPL/QA/10.2-F1).</p>
        </div>
        <Button variant="outline" onClick={() => nav(backPath)}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
      </div>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-slate-800">Non-Conformance Report</h2>
            <p className="text-xs text-slate-500 mt-0.5">Fields marked * are required to mark this NCR complete.</p>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">NCR No</div>
            <div className="font-mono text-lg font-extrabold text-blue-600">{ncrNo}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Report</div><div className="text-xs font-semibold text-slate-800 mt-0.5">{isStandalone ? "Standalone NCR" : `Report #${reportId}`}</div></div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Part Number</div><div className="text-xs font-semibold text-slate-800 mt-0.5">{context?.part_number || form.part_number || "—"}</div></div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Non-Conformance Source</div><div className="text-xs font-semibold text-slate-800 mt-0.5">{sourceLabel}</div></div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Actual Measurement</div><div className="text-xs font-semibold text-slate-800 mt-0.5">{context?.actual_measurement || context?.check_remarks || "—"}</div></div>
        </div>

        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><Label>Part Name *</Label><Input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} /></div>
          <div><Label>Part Number *</Label><Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} /></div>
          <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
          <div><Label>Date</Label><Input type="date" value={form.nc_date} onChange={(e) => setForm({ ...form, nc_date: e.target.value })} /></div>
          <div><Label>NC Raised By *</Label><Input value={form.raised_by_name} onChange={(e) => setForm({ ...form, raised_by_name: e.target.value })} /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="OPEN">Open</SelectItem><SelectItem value="CLOSED">Closed</SelectItem></SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4">
          <Label>Non-conformance Area (WHERE) *</Label>
          <div className="flex flex-wrap gap-4 pt-1.5">
            {NC_AREAS.map((area) => (
              <label key={area} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Checkbox checked={form.nc_area.includes(area)} onCheckedChange={() => toggleArea(area)} />
                {area}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4"><Label>Non-conformance Details (WHAT) *</Label><Textarea rows={3} value={form.nc_details} onChange={(e) => setForm({ ...form, nc_details: e.target.value })} /></div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div><Label>Actual Measurement</Label><Input value={form.actual_measurement} onChange={(e) => setForm({ ...form, actual_measurement: e.target.value })} /></div>
          <div><Label>Rejection Qty *</Label><Input type="number" step="any" value={form.rejection_qty} onChange={(e) => setForm({ ...form, rejection_qty: e.target.value })} /></div>
          <div><Label>Responsibility *</Label><Input value={form.responsibility} onChange={(e) => setForm({ ...form, responsibility: e.target.value })} /></div>
        </div>

        <div className="mt-4"><Label>Disposition Action Taken *</Label><Textarea rows={2} value={form.disposition_action} onChange={(e) => setForm({ ...form, disposition_action: e.target.value })} /></div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="mb-4"><h2 className="text-base font-bold text-slate-800">Root Cause Analysis</h2><p className="text-xs text-slate-500 mt-0.5">Optional at raise-time — typically completed as CAPA follow-up.</p></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Occurrence</Label><Textarea rows={2} value={form.rca_occurrence} onChange={(e) => setForm({ ...form, rca_occurrence: e.target.value })} /></div>
          <div><Label>Detection</Label><Textarea rows={2} value={form.rca_detection} onChange={(e) => setForm({ ...form, rca_detection: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-1 gap-3 mt-4">
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n}><Label>WHY : {n}</Label><Input value={form[`why${n}`]} onChange={(e) => setForm({ ...form, [`why${n}`]: e.target.value })} /></div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="text-base font-bold text-slate-800 mb-4">Action Plan</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Action Plan (HOW)</Label><Textarea rows={2} value={form.action_plan} onChange={(e) => setForm({ ...form, action_plan: e.target.value })} /></div>
          <div><Label>Responsibility (WHO)</Label><Input value={form.action_responsibility} onChange={(e) => setForm({ ...form, action_responsibility: e.target.value })} /></div>
          <div><Label>Target Date (WHEN)</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="text-base font-bold text-slate-800 mb-4">Effectiveness &amp; Deployment</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Effectiveness — Month 1</Label><Input value={form.effectiveness_month1} onChange={(e) => setForm({ ...form, effectiveness_month1: e.target.value })} /></div>
          <div><Label>Effectiveness — Month 2</Label><Input value={form.effectiveness_month2} onChange={(e) => setForm({ ...form, effectiveness_month2: e.target.value })} /></div>
          <div><Label>Effectiveness — Month 3</Label><Input value={form.effectiveness_month3} onChange={(e) => setForm({ ...form, effectiveness_month3: e.target.value })} /></div>
          <div><Label>Document Update — Document</Label><Input value={form.doc_update_document} onChange={(e) => setForm({ ...form, doc_update_document: e.target.value })} /></div>
          <div><Label>Document Update — Rev. No &amp; Date</Label><Input value={form.doc_update_revno} onChange={(e) => setForm({ ...form, doc_update_revno: e.target.value })} /></div>
          <div><Label>Horizontal Deployment</Label><Input value={form.horizontal_deployment} onChange={(e) => setForm({ ...form, horizontal_deployment: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div><Label>Prepared By</Label><Input value={form.prepared_by} onChange={(e) => setForm({ ...form, prepared_by: e.target.value })} /></div>
          <div><Label>Approved By</Label><Input value={form.approved_by} onChange={(e) => setForm({ ...form, approved_by: e.target.value })} /></div>
        </div>
      </section>

      <section className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => save(false)} disabled={savingDraft}>{savingDraft ? "Saving…" : "Save Draft"}</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => save(true)} disabled={savingComplete}>{savingComplete ? "Saving…" : "Save & Mark Complete"}</Button>
      </section>
      <p className="text-center text-[11px] text-slate-400 font-mono">CAPL/QA/10.2-F1 REV:00</p>
    </div>
  );
}
